/**
 * Surface projection — routes `inert/projection` rows to their target
 * surface (header / body / footer) without rendering the projection
 * row itself.
 *
 * Authored shape:
 *
 *   alias [calendar]
 *     projection [surface=header, preset=calendar-all-day]
 *       timeline[row]
 *         calendar-list
 *     timeline[column]
 *       calendar-list
 *
 * Projected shape (header surface gets the timeline[row] hoisted out
 * of the projection container; body keeps the alias and its non-
 * projection children):
 *
 *   header:
 *     timeline[row]
 *       calendar-list
 *   body:
 *     alias [calendar]
 *       timeline[column]
 *         calendar-list
 *   footer: (empty)
 *
 * The function is pure and operates on a generic `Row` shape: callers
 * supply a `Deps` that knows how to read children, recognize a
 * projection row, and read its surface/position attributes. The
 * returned `Node<Row>` tree preserves authored structure with
 * projection containers stripped — the consumer can walk this in
 * place of `row.children` to render body, or wrap the header/footer
 * surfaces' hoisted nodes for surface render.
 *
 * Property-list projection (`full` / `default` modes for the property
 * drawer) is unrelated and lives in `./tree-projection.ts` under
 * `PropertyListProjection`.
 */

import type { Option } from "../core/option.ts";
import { Json, type JsonObject } from "../core/json.ts";
import type { ModelRow } from "../rows/model-row.ts";
import { InertRow } from "../rows/variants/inert-row.ts";

/** The header / body / footer projection of `inert/projection` rows: which surface a row renders on. */
export namespace SurfaceProjection {
  /** The three legal surface targets for `inert/projection` rows. */
  export type Target = "header" | "body" | "footer";

  /** Every `Target`, for decoding one from a row's attributes. */
  export const TARGETS: readonly Target[] = ["header", "body", "footer"];

  /**
   * Attributes the projection row carries in its attribute bag.
   * `surface` is required for the row to participate in projection;
   * rows missing or carrying an unknown surface are ignored
   * (treated as inert and routed into body). `visible: false`
   * suppresses the projection — its children are dropped from the
   * projected output entirely (D6 hide path).
   */
  export interface Attributes {
    readonly surface: Option<Target>;
    readonly position: Option<number>;
    readonly visible: boolean;
  }

  /**
   * What `project` needs to know about the input row type. Mirrors
   * `PropertyListProjection.Deps` shape: pure, easy to satisfy from
   * `ModelRow` or a parsed-fixture row in tests.
   */
  export interface Deps<Row> {
    readonly childrenOf: (row: Row) => readonly Row[];
    readonly isProjection: (row: Row) => boolean;
    readonly attributesOf: (row: Row) => Attributes;
  }

  /**
   * A recursively projected row. Same identity as `row`, but
   * `children` is already projection-stripped — projection containers
   * have been removed and any nested projection-children hoisted to
   * their surfaces.
   */
  export interface Node<Row> {
    readonly row: Row;
    readonly children: readonly Node<Row>[];
  }

  /** One surface's hoisted+stripped children, in render order. */
  export interface Surface<Row> {
    readonly target: Target;
    readonly children: readonly Node<Row>[];
  }

  /** All three surfaces. Empty surfaces are still present. */
  export interface Result<Row> {
    readonly header: Surface<Row>;
    readonly body: Surface<Row>;
    readonly footer: Surface<Row>;
  }

  /**
   * Walk `rows` recursively. Every projection row's children are
   * hoisted to the surface named in `attributes.surface`; the
   * projection container itself is dropped (D2). Non-projection rows
   * appear in body with projection-stripped children — preserving
   * authored structure for the body surface.
   *
   * Sort rule (D5): rows hoisted into a surface are ordered by their
   * `attributes.position` ascending; ties broken by authored tree
   * order; unpositioned rows trail positioned ones in tree order.
   * Body rows keep their natural authored order (the position field
   * applies only to projection-hoisted siblings within a surface).
   */
  export function project<Row>(
    rows: readonly Row[],
    deps: Deps<Row>,
  ): Result<Row> {
    const buckets: Record<
      Exclude<Target, "body">,
      HoistedEntry<Row>[]
    > = { header: [], footer: [] };
    const bodyNodes: Node<Row>[] = [];
    let counter = 0;
    const nextOrder = () => counter++;
    for (const row of rows) {
      collect(row, deps, buckets, bodyNodes, nextOrder, null);
    }
    return {
      header: makeSurface("header", buckets.header),
      body: { target: "body", children: bodyNodes },
      footer: makeSurface("footer", buckets.footer),
    };
  }

  /**
   * Convenience: project a `ModelRow` tree using the standard
   * `inert/projection` discriminator and the row's `attributes()`
   * accessor.
   */
  export function projectSheet(
    rows: readonly ModelRow[],
  ): Result<ModelRow> {
    return project(rows, SHEET_ROW_DEPS);
  }
}

interface HoistedEntry<Row> {
  readonly node: SurfaceProjection.Node<Row>;
  readonly position: Option<number>;
  readonly authoredIndex: number;
}

/**
 * Recursive driver. Either:
 *  - The row is a projection: hoist each child as a stripped `Node`
 *    into the surface's bucket (carrying the projection's position).
 *  - The row is non-projection: build a stripped `Node` for it and
 *    push to the parent's body slot.
 */
function collect<Row>(
  row: Row,
  deps: SurfaceProjection.Deps<Row>,
  buckets: Record<
    Exclude<SurfaceProjection.Target, "body">,
    HoistedEntry<Row>[]
  >,
  bodyOut: SurfaceProjection.Node<Row>[],
  nextOrder: () => number,
  inheritedPosition: Option<number>,
): void {
  const childRows = deps.childrenOf(row);
  if (deps.isProjection(row)) {
    const attrs = deps.attributesOf(row);
    if (!attrs.visible) {
      // D6 hide path: a projection with `visible=false` drops its
      // children from the projected output entirely. The hidden
      // surface is still synthesized as an empty `surface` row by
      // `project` (D3 — stable layout slot), but no children
      // contribute.
      return;
    }
    const position = attrs.position ?? inheritedPosition;
    if (attrs.surface == null) {
      // No recognized surface: hoist children into body inline.
      for (const child of childRows) {
        collect(child, deps, buckets, bodyOut, nextOrder, position);
      }
      return;
    }
    if (attrs.surface === "body") {
      for (const child of childRows) {
        collect(child, deps, buckets, bodyOut, nextOrder, position);
      }
      return;
    }
    const surfaceBucket = buckets[attrs.surface];
    for (const child of childRows) {
      // For each hoisted child, recursively project it: its own
      // descendants may contain further projections, which surface
      // up to their own targets. The stripped node keeps the
      // child's body-side structure intact.
      const hoistedBody: SurfaceProjection.Node<Row>[] = [];
      collect(child, deps, buckets, hoistedBody, nextOrder, position);
      for (const node of hoistedBody) {
        surfaceBucket.push({
          node,
          position,
          authoredIndex: nextOrder(),
        });
      }
    }
    return;
  }
  // Non-projection row: build a stripped Node, push to body.
  const childNodes: SurfaceProjection.Node<Row>[] = [];
  for (const child of childRows) {
    collect(child, deps, buckets, childNodes, nextOrder, null);
  }
  bodyOut.push({ row, children: childNodes });
}

function makeSurface<Row>(
  target: Exclude<SurfaceProjection.Target, "body">,
  entries: readonly HoistedEntry<Row>[],
): SurfaceProjection.Surface<Row> {
  const sorted = [...entries].sort(compareEntries);
  return { target, children: sorted.map((e) => e.node) };
}

function compareEntries<Row>(
  a: HoistedEntry<Row>,
  b: HoistedEntry<Row>,
): number {
  const aHas = a.position != null;
  const bHas = b.position != null;
  if (aHas && !bHas) return -1;
  if (!aHas && bHas) return 1;
  if (aHas && bHas && a.position !== b.position) {
    return (a.position as number) - (b.position as number);
  }
  return a.authoredIndex - b.authoredIndex;
}

/**
 * Projection presets whose shown / hidden state a persisted attribute on the
 * PARENT decides, by preset → parent attribute (`true` = hidden). The calendar
 * all-day strip is the one: the calendar alias persists `hideAllDayEvents`
 * (its "All-day strip" option), and the strip — always hydrated, so its rows
 * and events survive a hide / show — follows it at render time. Nothing writes
 * the strip's own `visible` for that toggle, so no layer keeps a second copy
 * of the choice in sync by hand.
 */
const PARENT_VISIBILITY_ATTRS: Readonly<Record<string, string>> = {
  "calendar-all-day": "hideAllDayEvents",
};

/**
 * Whether a projection row renders, from its attributes and the live sheet:
 * hidden when its timescale is in `hideInTimescales`; else, for a preset in
 * `PARENT_VISIBILITY_ATTRS` whose parent carries that attribute, the parent's
 * attribute decides; else the row's own `visible` (default shown).
 */
export function projectionVisible(
  row: ModelRow,
  attrs: Option<JsonObject>,
): boolean {
  // A projection may declare timescales it is hidden in (e.g. the calendar
  // all-day strip hides in month, where all-day events fold into the grid's
  // day cells). Evaluated at render time against the live sheet timescale
  // (SheetViewModel keeps it synced), so it holds across hydration/forking
  // without the alias template re-running.
  const hiddenForTimescale =
    Json.strings(attrs, "hideInTimescales")?.includes(row.sheetReads.timescale) ??
      false;
  if (hiddenForTimescale) return false;
  const preset = Json.string(attrs, "preset");
  const parentAttr = preset == null ? null : PARENT_VISIBILITY_ATTRS[preset] ?? null;
  if (parentAttr != null) {
    const parent = row.parent;
    const hidden = parent == null ? null : Json.boolean(parent.attributes(), parentAttr);
    if (hidden != null) return !hidden;
  }
  return Json.boolean(attrs, "visible") !== false;
}

const SHEET_ROW_DEPS: SurfaceProjection.Deps<ModelRow> = {
  childrenOf: (row) => row.children,
  isProjection: (row) =>
    row instanceof InertRow && row.descriptor.kind === "projection",
  attributesOf: (row) => {
    const attrs = row.attributes();
    const validSurface = Json.oneOf(attrs, "surface", SurfaceProjection.TARGETS);
    const position = Json.number(attrs, "position");
    return { surface: validSurface, position, visible: projectionVisible(row, attrs) };
  },
};
